// ─── Step-scoped form validation ─────────────────────────────────────────────
//
// Validates only the fields belonging to the provided sections.
// Mirrors the same rules as ConsentFormRenderer.validateForm() but scoped
// to a specific step's sections. Respects conditional visibility — hidden
// sections and fields are skipped.
//
// Returns { valid: boolean, errors: { [fieldId]: string }, firstErrorFieldId: string|null }

import { isSectionVisible, isFieldVisible } from './ConsentFormRenderer';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Resolve a human-readable label for validation error messages
function resolveLabel(field) {
    return field.label || field.metadata?.display_label || field.name || field.placeholder || field.metadata?.placeholder || 'This field';
}

/**
 * Resolve the canonical field type string for validation purposes.
 * Prefers the metadata `frontend_type` over the raw `type` field.
 */
function resolveFieldType(field) {
    return field.metadata?.frontend_type || field.type || '';
}

/**
 * Validate all required / format rules for the given sections using the
 * current field values. Pass only the sections that belong to the current
 * step (already filtered) — the function won't apply further sectionIds
 * filtering.
 *
 * Conditional visibility is respected: sections and fields that are
 * conditionally hidden (via inline conditions or conditionalLogics array)
 * are skipped — their required rules won't block step advancement.
 *
 * @param {Array}  sections          - Array of section objects (pre-filtered to step scope)
 * @param {Object} fieldValues       - Current { fieldId: value } map
 * @param {Array}  [conditionalLogics] - Optional array of conditional logic rules
 * @param {Object} [leadData]        - Optional lead data for conditional evaluation
 * @returns {{ valid: boolean, errors: Object, firstErrorFieldId: string|null }}
 */
export function validateStep(sections, fieldValues, conditionalLogics, leadData) {
    const errors = {};

    (sections || []).forEach((section) => {
        // Skip conditionally hidden sections
        if (!isSectionVisible(section, conditionalLogics || [], fieldValues, leadData)) return;

        (section.fields || []).forEach((rawField) => {
            // Skip conditionally hidden fields
            if (!isFieldVisible(rawField, conditionalLogics || [], fieldValues, leadData)) return;
            const field = rawField;
            const fieldId = field.id || field.field_id;
            const fieldType = resolveFieldType(field);
            // Backend serializer returns `is_required`; some transformed
            // payloads use `required`. Read both so the requirement check
            // can't be silently bypassed when the API shape differs.
            const isRequired = !!(field.required ?? field.is_required);
            const validations = field.metadata?.validations || {};
            const customError = validations.custom_error;

            const val = fieldValues[fieldId];

            // ── Required checks ──────────────────────────────────────────────
            if (isRequired) {
                const fieldLabel = resolveLabel(field);
                if (fieldType === 'checkbox' && val !== true) {
                    errors[fieldId] = customError || `${fieldLabel} must be checked`;
                } else if (fieldType === 'checkbox_group' && (!val || val.length === 0)) {
                    errors[fieldId] = customError || `${fieldLabel} is required`;
                } else if (fieldType === 'signature' && !val) {
                    errors[fieldId] = customError || 'Signature is required';
                } else if (fieldType === 'toggle_ack' && val !== true) {
                    errors[fieldId] = customError || 'Please acknowledge this item';
                } else if (fieldType === 'toggle_detail' && !val?.agreed) {
                    errors[fieldId] = customError || `${fieldLabel} must be acknowledged`;
                } else if (fieldType === 'toggle_text' && !val?.enabled) {
                    errors[fieldId] = customError || `${fieldLabel} must be acknowledged`;
                } else if (!['checkbox', 'checkbox_group', 'signature', 'toggle_ack', 'toggle_detail', 'toggle_text'].includes(fieldType)) {
                    if (!val && val !== 0) {
                        errors[fieldId] = customError || `${fieldLabel} is required`;
                    }
                }
            }

            // Skip format validations for object-valued fields and empty values
            if (fieldType === 'toggle_detail' || fieldType === 'toggle_text') return;
            if (!val && val !== 0) return;

            // ── Format validations ──────────────────────────────────────────
            if (fieldType === 'email' && !EMAIL_REGEX.test(String(val))) {
                errors[fieldId] = customError || 'Please enter a valid email address';
            }

            if (validations.pattern?.value && typeof val === 'string') {
                try {
                    const flags = validations.pattern.type === 'uk_postcode' ? 'i' : '';
                    const rx = new RegExp(validations.pattern.value, flags);
                    if (!rx.test(val)) {
                        errors[fieldId] = validations.pattern.message || 'Invalid format';
                    }
                } catch {
                    // Invalid regex pattern — skip silently
                }
            }

            if (validations.match_field?.field_id && typeof val === 'string') {
                const matchId = validations.match_field.field_id;
                // Field IDs may be stored as string or number — try all variants
                const targetVal =
                    fieldValues[matchId] ??
                    fieldValues[Number(matchId)] ??
                    fieldValues[String(matchId)];
                if (targetVal !== undefined && val !== targetVal) {
                    errors[fieldId] = validations.match_field.message || 'Values do not match';
                }
            }

            if (validations.min_length?.value && typeof val === 'string' &&
                val.length < validations.min_length.value) {
                errors[fieldId] =
                    validations.min_length.message ||
                    `Must be at least ${validations.min_length.value} characters`;
            }

            if (validations.max_length?.value && typeof val === 'string' &&
                val.length > validations.max_length.value) {
                errors[fieldId] =
                    validations.max_length.message ||
                    `Cannot exceed ${validations.max_length.value} characters`;
            }

            // Number range validation
            if (fieldType === 'number') {
                const numVal = Number(val);
                if (!Number.isNaN(numVal)) {
                    const minVal = validations.min_value?.value;
                    const maxVal = validations.max_value?.value;
                    if (minVal !== undefined && numVal < Number(minVal)) {
                        errors[fieldId] = validations.min_value?.message || `Must be at least ${minVal}`;
                    } else if (maxVal !== undefined && numVal > Number(maxVal)) {
                        errors[fieldId] = validations.max_value?.message || `Cannot exceed ${maxVal}`;
                    }
                }
            }
        });
    });

    const errorKeys = Object.keys(errors);
    return {
        valid: errorKeys.length === 0,
        errors,
        firstErrorFieldId: errorKeys[0] || null,
    };
}
